import { useEffect, useRef, useState } from 'react'
import { apiClient } from '@/services/apiClient'

/**
 * SA-979 / SA-981: signed links to a stored file. A download link (no body,
 * 60 seconds) is followed at once. A view link (an hour) feeds the viewer's
 * frame, player or image. Both go through `apiClient`, so the Bearer header
 * and the refresh rule apply.
 */
export type LinkPurpose = 'download' | 'view'

export interface MediaLink {
  url: string
  /** Epoch milliseconds after which the server refuses the URL. */
  expiresAt: number
}

/** The mint answered 404: the message or its stored file is gone. */
export class MediaGoneError extends Error {
  constructor() {
    super('media gone')
    this.name = 'MediaGoneError'
  }
}

/** SA-1122: a mint that has not answered in this long is given up: the
 * request is aborted and the caller reads a failure (Try again), so a hung
 * renewal never keeps the player's in-flight guard set. The timer races the
 * request rather than trusting the abort alone, because `apiClient`'s token
 * refresh does not take the signal. */
export const MINT_TIMEOUT_MS = 30_000

export async function mintMediaLink(mediaUrl: string, purpose: LinkPurpose): Promise<MediaLink> {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const timedOut = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort()
      reject(new Error('media link mint timed out'))
    }, MINT_TIMEOUT_MS)
  })
  try {
    return await Promise.race([mint(mediaUrl, purpose, controller.signal), timedOut])
  } finally {
    clearTimeout(timer)
  }
}

async function mint(mediaUrl: string, purpose: LinkPurpose, signal: AbortSignal): Promise<MediaLink> {
  const res = purpose === 'view'
    ? await apiClient.fetch(`${mediaUrl}/link`, { method: 'POST', body: JSON.stringify({ purpose: 'view' }), signal })
    : await apiClient.fetch(`${mediaUrl}/link`, { method: 'POST', signal })
  if (res.status === 404) throw new MediaGoneError()
  if (!res.ok) throw new Error(`status ${res.status}`)
  const { url, expires_in } = (await res.json()) as { url: string; expires_in: number }
  return { url, expiresAt: Date.now() + expires_in * 1000 }
}

/** Follow a download link through a temporary <a download>, so the browser
 * streams the file to disk with its own progress, at any size. It is attached
 * before the click: Safari has ignored clicks on detached anchors. */
export function followDownload(url: string, name: string): void {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

export type DownloadState = 'idle' | 'preparing' | 'gone' | 'failed'

/** One download, minted on demand: the thread's download button and the viewer's Download. */
export function useMediaDownload(messageId: string, mediaUrl: string, filename?: string | null) {
  // The state belongs to the file it was set for: a viewer that steps to
  // another file reads idle until that file's own Download is pressed.
  const [held, setHeld] = useState<{ for: string; state: DownloadState }>({ for: mediaUrl, state: 'idle' })
  const state: DownloadState = held.for === mediaUrl ? held.state : 'idle'

  async function download(): Promise<void> {
    setHeld({ for: mediaUrl, state: 'preparing' })
    try {
      const { url } = await mintMediaLink(mediaUrl, 'download')
      followDownload(url, filename || `attachment-${messageId}`)
      setHeld({ for: mediaUrl, state: 'idle' })
    } catch (err) {
      setHeld({ for: mediaUrl, state: err instanceof MediaGoneError ? 'gone' : 'failed' })
    }
  }

  return { state, download }
}

/** SA-981 (design note §4.3): a link counts as expired this long before its
 * `exp`, so a request still in flight at the boundary renews. */
export const RENEW_MARGIN_MS = 5_000

/** `MediaError.MEDIA_ERR_NETWORK`: a network drop while fetching, not an
 * unplayable file. Named here because happy-dom defines no `MediaError`. */
const MEDIA_ERR_NETWORK = 2

export type PlayableStatus = 'idle' | 'opening' | 'ready' | 'gone' | 'failed' | 'unplayable'

export interface PlayableLinkOptions {
  /** False keeps the hook idle and mints nothing (a lazy player before it is seen). */
  enabled?: boolean
  /** Bytes already loaded in the thread: served as they are, no link minted. */
  objectUrl?: string | null
}

/**
 * SA-1094 (SA-981 §4.2-4.3, lifted from the dialog): the one mint-and-renew
 * rule for a player. A view link is minted when enabled, dropped and minted
 * again when `mediaUrl` moves. On a player error after the link expired
 * (with the margin) it mints once more and resumes from the same second; a
 * renewed link failing again before its own expiry, or any error before
 * expiry, is `unplayable` (`failed` on a network error, which Try again
 * answers). Two errors during one pending renewal renew once.
 */
export function usePlayableLink(mediaUrl: string, { enabled = true, objectUrl = null }: PlayableLinkOptions = {}) {
  const [link, setLink] = useState<MediaLink | null>(null)
  const [status, setStatus] = useState<PlayableStatus>(objectUrl ? 'ready' : enabled ? 'opening' : 'idle')
  const [attempt, setAttempt] = useState(0)
  const [renewals, setRenewals] = useState(0)
  const resume = useRef<{ time: number; playing: boolean } | null>(null)
  const mounted = useRef(true)
  const current = useRef(mediaUrl)
  // SA-1110 (from SA-1019): the file a renewal mint is in flight for. Two
  // `error` events during one pending mint renew once.
  const renewing = useRef<string | null>(null)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    current.current = mediaUrl
    resume.current = null
    setRenewals(0)
    if (objectUrl) {
      setLink(null)
      setStatus('ready')
      return
    }
    if (!enabled) {
      setLink(null)
      setStatus('idle')
      return
    }
    let cancelled = false
    setLink(null)
    setStatus('opening')
    mintMediaLink(mediaUrl, 'view')
      .then(minted => {
        if (cancelled) return
        setLink(minted)
        setStatus('ready')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setLink(null)
        setStatus(err instanceof MediaGoneError ? 'gone' : 'failed')
      })
    return () => {
      cancelled = true
    }
  }, [mediaUrl, objectUrl, enabled, attempt])

  async function onError(element: HTMLMediaElement | null): Promise<void> {
    if (renewing.current === mediaUrl) return
    if (!link || Date.now() < link.expiresAt - RENEW_MARGIN_MS) {
      setStatus(link && element?.error?.code === MEDIA_ERR_NETWORK ? 'failed' : 'unplayable')
      return
    }
    resume.current = element ? { time: element.currentTime, playing: !element.paused } : null
    const url = mediaUrl
    renewing.current = url
    try {
      const fresh = await mintMediaLink(url, 'view')
      if (!mounted.current || url !== current.current) return
      setRenewals(n => n + 1)
      setLink(fresh)
    } catch (err) {
      if (!mounted.current || url !== current.current) return
      setLink(null)
      setStatus(err instanceof MediaGoneError ? 'gone' : 'failed')
    } finally {
      if (renewing.current === url) renewing.current = null
    }
  }

  function onLoadedMetadata(element: HTMLMediaElement): void {
    const saved = resume.current
    if (!saved) return
    resume.current = null
    element.currentTime = saved.time
    if (saved.playing) element.play().catch(() => {})
  }

  return {
    src: objectUrl ?? link?.url ?? null,
    link,
    status,
    renewals,
    retry: () => setAttempt(n => n + 1),
    onError,
    onLoadedMetadata,
  }
}
